import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { Tooltip, Zoom } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import { AxiosError } from "axios";
import { type ReactNode, useEffect, useRef, useState } from "react";

import { CopyButton } from "./CopyButton";
import { CODE_FONT, groupDigits, TRUNCATE } from "./TotpCode";

const POLL_INTERVAL_MS = 5000;
const MAX_POLL_DURATION_MS = 180000;

const PIPELINE_WARNING =
  "A retrieval is currently running for this practice + payer. Using this code may break the automated run.";
const LOCKED_WARNING =
  "CAMI is fetching MFA for this payer right now — try again in a few minutes.";

type Phase = "idle" | "polling" | "found" | "locked" | "timeout" | "error";

function formatAge(seconds: number): string {
  return seconds < 60 ? `${seconds}s ago` : `${Math.floor(seconds / 60)}m ago`;
}

export function EmailMfaCode({
  fetchCode,
}: {
  fetchCode: () => Promise<EmailMfaMessage>;
}) {
  const [phase, setPhase] = useState<Phase>("idle");
  const [code, setCode] = useState<string | null>(null);
  const [ageSeconds, setAgeSeconds] = useState(0);
  const [pipelineRunning, setPipelineRunning] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const deadlineRef = useRef(0);
  const fetchRef = useRef(fetchCode);
  fetchRef.current = fetchCode;

  const stopPolling = () => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const poll = async () => {
    try {
      const res = await fetchRef.current();
      setPipelineRunning(res.pipeline_running);
      if (res.status === "LOCKED") {
        stopPolling();
        setPhase("locked");
      } else if (res.status === "FOUND") {
        stopPolling();
        setCode(res.code ?? null);
        setAgeSeconds(res.age_seconds ?? 0);
        setPhase("found");
      } else if (Date.now() >= deadlineRef.current) {
        stopPolling();
        setPhase("timeout");
      }
    } catch (error) {
      stopPolling();
      let message = "Failed to load code";
      if (error instanceof AxiosError) {
        if (error.response?.status === 404) {
          message = "Not supported for this payer yet";
        } else if (error.response?.status === 409) {
          message = "MFA mailbox not connected";
        }
      }
      setErrorMsg(message);
      setPhase("error");
    }
  };

  const start = () => {
    stopPolling();
    setCode(null);
    setErrorMsg(null);
    setPipelineRunning(false);
    setAgeSeconds(0);
    setPhase("polling");
    deadlineRef.current = Date.now() + MAX_POLL_DURATION_MS;
    poll();
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS);
  };

  const reset = () => {
    stopPolling();
    setCode(null);
    setPipelineRunning(false);
    setPhase("idle");
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding stopPolling would change when this runs
  useEffect(() => stopPolling, []);

  useEffect(() => {
    if (phase !== "found") return undefined;
    const ageTimer = setInterval(() => setAgeSeconds((prev) => prev + 1), 1000);
    return () => clearInterval(ageTimer);
  }, [phase]);

  const pipelineWarning = pipelineRunning ? (
    <Tooltip
      title={PIPELINE_WARNING}
      TransitionComponent={Zoom}
      placement="top"
      arrow
    >
      <Box component="span" sx={{ display: "inline-flex" }}>
        <WarningAmberOutlinedIcon
          sx={{ width: 18, height: 18, color: "#B54708" }}
        />
      </Box>
    </Tooltip>
  ) : null;

  let content: ReactNode;
  if (phase === "idle") {
    content = (
      <Button
        variant="outlined"
        size="small"
        onClick={start}
        sx={{ textTransform: "none", fontSize: 13, padding: "2px 10px" }}
      >
        Get MFA code
      </Button>
    );
  } else if (phase === "polling") {
    content = (
      <Box
        sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}
      >
        <CircularProgress size={16} thickness={5} sx={{ color: "#475467" }} />
        <Box sx={{ ...TRUNCATE, color: "#475467", fontSize: 13 }}>
          Waiting for email…
        </Box>
        {pipelineWarning}
        <IconButton
          aria-label="cancel email mfa"
          size="small"
          sx={{ color: "#475467", padding: "5px", flexShrink: 0 }}
          onClick={reset}
        >
          <CloseOutlinedIcon sx={{ width: "20px", height: "20px" }} />
        </IconButton>
      </Box>
    );
  } else if (phase === "locked") {
    content = (
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <Tooltip
          title={LOCKED_WARNING}
          TransitionComponent={Zoom}
          placement="top"
          arrow
        >
          <Box component="span" sx={{ display: "inline-flex" }}>
            <WarningAmberOutlinedIcon
              sx={{ width: 20, height: 20, color: "#B54708" }}
            />
          </Box>
        </Tooltip>
        <Button
          variant="text"
          size="small"
          onClick={start}
          sx={{ textTransform: "none", fontSize: 13 }}
        >
          Retry
        </Button>
      </Box>
    );
  } else if (phase === "timeout") {
    content = (
      <Box
        sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}
      >
        <Box sx={{ ...TRUNCATE, color: "#B42318", fontSize: 13 }}>
          No code found — did you trigger the MFA on the portal?
        </Box>
        <Button
          variant="text"
          size="small"
          onClick={start}
          sx={{ textTransform: "none", fontSize: 13 }}
        >
          Retry
        </Button>
      </Box>
    );
  } else if (phase === "error") {
    content = (
      <Tooltip
        title={errorMsg ?? ""}
        TransitionComponent={Zoom}
        placement="top"
        arrow
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            color: "#B42318",
            fontSize: 13,
          }}
        >
          <WarningAmberOutlinedIcon sx={{ width: 18, height: 18 }} />
          {errorMsg}
        </Box>
      </Tooltip>
    );
  } else {
    content = (
      <Box
        sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}
      >
        <Box
          data-sr-redact
          sx={{ ...CODE_FONT, ...TRUNCATE, color: "#101828" }}
        >
          {code ? groupDigits(code) : "…"}
        </Box>
        <Box sx={{ ...TRUNCATE, color: "#98A2B3", fontSize: 12 }}>
          received {formatAge(ageSeconds)}
        </Box>
        {pipelineWarning}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        width: "100%",
        justifyContent: "space-between",
      }}
    >
      {content}
      {phase === "found" && (
        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
          {code && <CopyButton getText={() => code} />}
          <IconButton
            aria-label="hide email mfa"
            size="small"
            sx={{ color: "#475467", padding: "5px" }}
            onClick={reset}
          >
            <VisibilityOffOutlinedIcon sx={{ width: "20px", height: "20px" }} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
}
